/*
  Free gift with every order (Theme settings > Cart > "Free gift with every
  order", settings.freebie).

  Keeps exactly one gift in any cart that holds something that ships, and none
  in a cart that does not. The gift is a $0 variant, so this only manages the
  line; nothing here touches price.

  The gift travels with the customer's own add to cart: when the cart has no
  gift yet, it is appended to that /cart/add.js request, so the drawer's first
  render already shows it. Shopify adds a multi-item request all or nothing,
  so if the gift is refused (sold out, not sold in this market) the original
  request is sent again without it and the customer's product still goes in.
  The product form sends a single item as FormData, which cannot carry a
  second line, so it asks jsonAddBody() for an items body first. Removal
  works the same way in reverse: when the customer removes the last product,
  the theme's quantity handler asks removalFor() and sends one
  /cart/update.js that removes the gift with it.

  The gift row has its own trash icon. Removing the gift that way declines
  it: the same request sets the cart attribute _sk_gift_declined, and from
  then on nothing here adds the gift back to that cart.

  Everything else is caught afterwards. Two things start a check:
  - every cart write made by the theme or an app through fetch, and
  - a cart render whose Liquid marker (snippets/sk-cart-gift.liquid) says
    "fix", which also covers page loads and apps that write with jQuery.
  A check is one /cart.js read and, only when the cart is wrong, one write.
  The drawer is redrawn from that write's response; the rest of the page
  (the cart page) refreshes through the theme's own cartUpdate event.

  The checkout button waits for a pending check, so an order cannot leave
  between a product being added and its gift arriving. A failed write never
  blocks checkout: the order goes through without the gift.

  Loaded as an external file from layout/theme.liquid: the storefront's
  instant-loader app never runs inline scripts it swaps into the page.
*/
(() => {
  if (window.SiamkickGift) return;

  const script =
    document.currentScript || document.querySelector("script[data-sk-gift-variant]");
  const giftId = Number(script && script.dataset.skGiftVariant) || 0;

  const properties = (item) => item.properties || {};
  const isGift = (item) => giftId !== 0 && Number(item.variant_id) === giftId;
  // A line this script added for a gift the merchant has since replaced.
  const isOldGift = (item) => !isGift(item) && Boolean(properties(item)._sk_gift);
  // Customizer decoration lines only exist beside their shorts, and the
  // customizer removes them when the shorts go, so they never keep the gift.
  const isDecoration = (item) => properties(item)._sk_role === "decoration";
  // What the customer would count: no gift, and no decoration lines
  // (customizer decorations and quick flags are drawn inside their shorts).
  const visibleCount = (cart) =>
    ((cart && cart.items) || []).reduce(
      (total, item) => (isGift(item) || isDecoration(item) ? total : total + item.quantity),
      0,
    );
  const giftLine = () => ({ id: giftId, quantity: 1, properties: { _sk_gift: "1" } });

  const DECLINED = "_sk_gift_declined";
  const isDeclined = (cart) => Boolean(cart && cart.attributes && cart.attributes[DECLINED]);

  // What the page last knew about the gift in the cart: its quantity (null
  // when unknown), whether it can be added and whether the customer removed
  // it. Updated from every cart render and every cart read, and
  // optimistically when an add carries the gift or the customer removes it.
  const known = { qty: null, available: null, declined: false };
  const needsGift = () =>
    giftId !== 0 && known.qty === 0 && known.available !== false && !known.declined;

  // The product form's FormData as a JSON items body, for an add that should
  // bring the gift (the fetch wrapper below appends it). Null when the gift
  // is not needed or the form holds a file, so the form sends as it always has.
  // Extra lines the form carries as items[n][...] fields (the quick flag addon,
  // sections/ss-product-addons-2.liquid) ride along after the main line, so
  // they are not lost when the add switches to JSON.
  function jsonAddBody(formData) {
    if (!needsGift() || !formData || typeof formData.entries !== "function") return null;
    const line = { properties: {} };
    const extras = new Map();
    const extra = (index) => {
      if (!extras.has(index)) extras.set(index, { properties: {} });
      return extras.get(index);
    };
    const body = {};
    for (const [key, value] of formData.entries()) {
      if (typeof value !== "string") return null;
      // items[n][id], items[n][quantity], items[n][properties][Key] and the
      // addon's own spelling items[n][properties[Key]].
      const item = key.match(/^items\[(\d+)\]\[(id|quantity|properties)\](?:\[([^\]]+)\])?$/)
        || key.match(/^items\[(\d+)\]\[(properties)\[([^\]]+)\]\]$/);
      const property = key.match(/^properties\[(.+)\]$/);
      if (item) {
        const target = extra(Number(item[1]));
        if (item[2] === "id") target.id = Number(value);
        else if (item[2] === "quantity") target.quantity = Number(value) || 1;
        else if (item[3]) target.properties[item[3]] = value;
      } else if (property) line.properties[property[1]] = value;
      else if (key === "id") line.id = Number(value);
      else if (key === "quantity") line.quantity = Number(value) || 1;
      else if (key === "selling_plan") {
        if (value) line.selling_plan = Number(value);
      } else if (key === "sections" || key === "sections_url") body[key] = value;
    }
    if (!line.id) return null;
    if (!line.quantity) line.quantity = 1;
    const others = [...extras.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, value]) => value)
      .filter((value) => value.id)
      .map((value) => ({ ...value, quantity: value.quantity || 1 }));
    return JSON.stringify({ ...body, items: [line, ...others] });
  }

  // For the theme's quantity handler (assets/cart.js): the /cart/update.js
  // body to send instead of its usual /cart/change.js, or null to send that.
  // - The customer removes the gift itself: remove it and record that they
  //   declined it, in one request, so it is not added straight back.
  // - Removing `line` would leave nothing but the gift: remove both at once,
  //   so the cart empties instead of showing the gift alone for a moment.
  // Read from the rows the customer is looking at.
  function removalFor(container, line, quantity) {
    if (!giftId || Number(quantity) !== 0 || !container) return null;
    const rows = Array.from(container.querySelectorAll(".cart-item[data-line-key]"));
    const target = rows.find((row) => {
      const match = row.id.match(/-(\d+)$/);
      return match && Number(match[1]) === Number(line);
    });
    const gift = rows.find((row) => row.classList.contains("cart-item--gift"));
    if (!target || !gift) return null;
    if (target === gift) {
      known.declined = true;
      return { updates: { [gift.dataset.lineKey]: 0 }, attributes: { [DECLINED]: "1" } };
    }
    const kept = rows.filter((row) => row !== gift && !row.hasAttribute("data-sk-decoration"));
    if (kept.length !== 1 || kept[0] !== target) return null;
    return { updates: { [target.dataset.lineKey]: 0, [gift.dataset.lineKey]: 0 } };
  }

  window.SiamkickGift = { variantId: giftId, isGift, visibleCount, jsonAddBody, removalFor };
  if (!giftId) return;

  const OWN_REQUEST = "X-SK-Gift";
  // A hard stop for a page where something keeps undoing the gift. High
  // enough that a shopper adding and removing things all session on one page
  // never reaches it.
  const MAX_WRITES = 30;

  let timer = null;
  let running = null;
  let rerun = false;
  let dirty = false;
  let writes = 0;
  let bypass = false;
  // The cart this script last tried to fix. Meeting the same cart again with
  // nothing else having written in between means the fix did not take (sold
  // out, not sold in this market, clamped), so it is not retried.
  let lastAttempt = null;
  let staleRedrawn = null;

  const root = () => {
    const value = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return String(value || "/").replace(/\/?$/, "/");
  };

  const cartSignature = (cart) =>
    cart.items.map((item) => `${item.key}:${item.quantity}`).join("|");

  function planFor(cart) {
    const items = cart.items || [];
    const ships = items.some(
      (item) =>
        !isGift(item) && !isOldGift(item) && !isDecoration(item) && item.requires_shipping,
    );
    const want = ships && !isDeclined(cart) ? 1 : 0;
    const updates = {};
    items.filter(isOldGift).forEach((item) => {
      updates[item.key] = 0;
    });
    const lines = items.filter(isGift);
    // Keep the line this script tagged, so the customizer app still recognises
    // it; any other copy of the gift goes.
    const keeper = lines.find((item) => properties(item)._sk_gift) || lines[0];
    lines.forEach((item) => {
      const target = item === keeper ? want : 0;
      if (item.quantity !== target) updates[item.key] = target;
    });
    const add = want === 1 && lines.length === 0;
    if (!add && Object.keys(updates).length === 0) return null;
    return { add, updates, want };
  }

  // Each write asks for the freshly rendered drawer in the same request, so
  // the gift shows up without a second round trip.
  function post(path, body) {
    return window
      .fetch(root() + path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          [OWN_REQUEST]: "1",
        },
        body: JSON.stringify({
          ...body,
          sections: ["cart-drawer"],
          sections_url: window.location.pathname,
        }),
      })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  // Returns the response of the last write, or null on failure.
  async function apply(plan) {
    let result = {};
    if (Object.keys(plan.updates).length) {
      result = await post("cart/update.js", { updates: plan.updates });
      if (!result) return null;
    }
    if (plan.add) {
      result = await post("cart/add.js", { items: [giftLine()] });
      if (!result) return null;
    }
    return result;
  }

  // Swaps the drawer the same way the theme's CartDrawerItems.onCartUpdate
  // does, without opening it: a gift fixed on page load must not pop the
  // drawer open. The publish then refreshes everything else (the cart page);
  // it names the drawer as its source so the drawer does not render twice.
  function drawDrawer(html) {
    const drawer = document.querySelector("cart-drawer");
    const current = document.getElementById("CartDrawer");
    if (!drawer || !current || typeof html !== "string") return false;
    const source = new DOMParser().parseFromString(html, "text/html");
    const fresh = source.getElementById("CartDrawer");
    const freshDrawer = source.querySelector("cart-drawer");
    if (!fresh || !freshDrawer) return false;
    // Replacing the markup disconnects the old <cart-drawer-items>, whose
    // disconnectedCallback aborts any theme refresh still in flight.
    current.innerHTML = fresh.innerHTML;
    drawer.classList.toggle("is-empty", freshDrawer.classList.contains("is-empty"));
    if (typeof drawer.bindOverlay === "function") drawer.bindOverlay();
    if (typeof drawer.setHeaderCartIconAccessibility === "function") {
      drawer.setHeaderCartIconAccessibility();
    }
    if (typeof window.SiamkickCurrencyRefresh === "function") {
      window.SiamkickCurrencyRefresh(drawer);
    }
    document.dispatchEvent(new CustomEvent("cart:drawer-rendered"));
    return true;
  }

  // `result` is the write's response: /cart/update.js answers with the whole
  // cart, so it knows when removing the gift emptied it. The cart page only
  // learns that from its own quantity handler, which saw the gift still there.
  function refresh(result) {
    if (result && typeof result.item_count === "number") {
      const empty = result.item_count === 0;
      document.querySelectorAll("cart-items, #main-cart-footer").forEach((element) => {
        element.classList.toggle("is-empty", empty);
      });
    }
    const sections = result && result.sections;
    const drawn = drawDrawer(sections && sections["cart-drawer"]);
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: drawn ? "cart-drawer-items" : "sk-cart-gift",
      });
    }
  }

  function reconcile() {
    window.clearTimeout(timer);
    timer = null;
    if (running) {
      rerun = true;
      return running;
    }
    running = (async () => {
      try {
        do {
          rerun = false;
          dirty = false;
          const response = await window.fetch(`${root()}cart.js`, {
            headers: { Accept: "application/json", [OWN_REQUEST]: "1" },
          });
          if (!response.ok) break;
          const cart = await response.json();
          known.qty = cart.items.filter(isGift).reduce((total, item) => total + item.quantity, 0);
          known.declined = isDeclined(cart);
          const plan = planFor(cart);
          const signature = cartSignature(cart);
          if (!plan) {
            lastAttempt = null;
            // The cart is right but the page still shows a render that says
            // otherwise: a refresh that started before the gift was written
            // landed after it. Re-render once for this cart, never in a loop.
            if (
              signature !== staleRedrawn &&
              document.querySelector('[data-sk-gift-state="fix"]')
            ) {
              staleRedrawn = signature;
              refresh(null);
            }
            continue;
          }
          if (signature === lastAttempt || writes >= MAX_WRITES) continue;
          lastAttempt = signature;
          writes += 1;
          const result = await apply(plan);
          if (result) {
            known.qty = plan.want;
            refresh(result);
          }
        } while (rerun);
      } catch (error) {
        // A failed check leaves the cart as the customer made it.
      } finally {
        running = null;
      }
    })();
    return running;
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(reconcile, 60);
  }

  // An add that should bring the gift: a JSON body with an items list that
  // does not already hold it.
  function withGift(body) {
    try {
      const parsed = JSON.parse(body);
      if (!parsed || !Array.isArray(parsed.items) || !parsed.items.length) return null;
      if (parsed.items.some((item) => Number(item.id) === giftId)) return null;
      return JSON.stringify({ ...parsed, items: [...parsed.items, giftLine()] });
    } catch (error) {
      return null;
    }
  }

  // Cart writes by anything else on the page. Wrapped rather than replaced, so
  // the customizer app's own fetch wrapper keeps seeing every request.
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const CART_ADD = /\/cart\/add(\.js)?$/;
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const args = arguments;
    let own = false;
    let path = "";
    let method = "GET";
    try {
      const headers = (init && init.headers) || (input && input.headers);
      own = Boolean(
        headers &&
          (typeof headers.get === "function" ? headers.get(OWN_REQUEST) : headers[OWN_REQUEST]),
      );
      const url = typeof input === "string" ? input : input && (input.url || String(input));
      path = new URL(url, window.location.origin).pathname;
      method = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
    } catch (error) {
      path = "";
    }
    if (own || !CART_WRITE.test(path)) return nativeFetch.apply(window, args);

    dirty = true;
    lastAttempt = null;
    if (/\/cart\/clear(\.js)?$/.test(path)) known.qty = 0;

    let request;
    const giftBody =
      method === "POST" &&
      CART_ADD.test(path) &&
      typeof (input && input.url) !== "string" &&
      init &&
      typeof init.body === "string" &&
      needsGift()
        ? withGift(init.body)
        : null;
    if (giftBody) {
      // Counted as in the cart straight away, so a second add fired before
      // this one answers does not carry another.
      known.qty = 1;
      request = nativeFetch
        .call(window, input, { ...init, body: giftBody })
        .then((response) => {
          if (response.ok) return response;
          // All or nothing: nothing was added, so the customer's own request
          // goes again exactly as they sent it.
          known.qty = 0;
          return nativeFetch.apply(window, args);
        })
        .catch((error) => {
          known.qty = null;
          throw error;
        });
    } else {
      request = nativeFetch.apply(window, args);
    }
    request.then(schedule, schedule);
    return request;
  };

  // Every cart render carries a fresh marker; only new ones are read, so the
  // customizer's repaints of existing rows cost nothing here.
  const seen = new WeakSet();
  function scan() {
    document.querySelectorAll("[data-sk-gift-state]").forEach((marker) => {
      if (seen.has(marker)) return;
      seen.add(marker);
      const qty = Number(marker.dataset.skGiftQty);
      if (Number.isFinite(qty)) known.qty = qty;
      known.available = marker.dataset.skGiftAvailable !== "false";
      known.declined = marker.dataset.skGiftDeclined === "true";
      if (marker.dataset.skGiftState === "fix") schedule();
    });
  }
  let scanQueued = false;
  new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(() => {
      scanQueued = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  scan();

  const needsCheck = () =>
    timer !== null ||
    running !== null ||
    dirty ||
    Boolean(document.querySelector('[data-sk-gift-state="fix"]'));

  // Captured before the theme's own handlers. The drawer re-renders its form
  // while a check runs, so the submit is replayed on whichever form and button
  // are live afterwards; a button the customizer has disabled stays unpressed.
  document.addEventListener(
    "submit",
    (event) => {
      const submitter = event.submitter;
      if (bypass || !submitter || submitter.name !== "checkout" || !needsCheck()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const form = event.target;
      const formId = form.id;
      const buttonId = submitter.id;
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 4000));
      Promise.race([reconcile(), timeout]).then(() => {
        const liveForm = (formId && document.getElementById(formId)) || form;
        const button =
          (buttonId && document.getElementById(buttonId)) ||
          (formId && document.querySelector(`[name="checkout"][form="${formId}"]`)) ||
          liveForm.querySelector('[name="checkout"]');
        if (!liveForm.isConnected || !button || button.disabled) return;
        bypass = true;
        try {
          liveForm.requestSubmit(button);
        } finally {
          bypass = false;
        }
      });
    },
    true,
  );
})();
